/**
 * IndexedDB-backed store for attachment background upload records.
 *
 * Web port of `kaether_for_ios/Stoat/E2EE/AttachmentBackgroundUploadStore.swift`.
 *
 * ## Record-per-attachmentId
 *
 * Each attachment has exactly one upload record, keyed by `attachmentId`.
 * Creating a new record for an already-known `attachmentId` is idempotent
 * (returns the existing record unchanged).
 *
 * ## `bindToMessageId` invariant
 *
 * An attachment is bound to exactly one `messageId`. Calling
 * `bindToMessageId(attachmentId, differentMessageId)` when a binding already
 * exists with a DIFFERENT messageId throws an error — once bound, an
 * attachment cannot be re-bound. Calling it with the SAME messageId is
 * idempotent (returns the existing binding).
 *
 * This invariant is enforced at the store layer, not in the state machine, so
 * the event types stay clean: no event carries a `messageId` field, and future
 * event additions will not accidentally miss updating the binding.
 *
 * ## Persistence
 *
 * All records are stored in IndexedDB. The `dbName` parameter allows test
 * isolation via unique per-test database names.
 *
 * ## No encryption
 *
 * iOS uses `.completeFileProtection`; the web equivalent would require a
 * non-extractable CryptoKey + IndexedDB. As with every other web E2EE store,
 * this stores plaintext in IDB. The platform gap is documented in
 * `Signal_standard_web.md`.
 */

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const DEFAULT_DB_NAME = "kaether:attachment-uploads:v1";
const STORE_RECORDS = "upload_records";
const STORE_BINDINGS = "message_bindings";
const DB_VERSION = 1;

// ---------------------------------------------------------------------------
// Stored record shapes
// ---------------------------------------------------------------------------

/** Serialised form of `AttachmentBackgroundUploadState` stored in IDB. */
export interface StoredUploadRecord {
  /** IDB primary key. */
  attachmentId: string;
  /** Serialised state as a JSON-compatible object. */
  stateJson: string;
}

/** Mapping from attachmentId to messageId (the `bindToMessageId` invariant). */
export interface StoredBinding {
  /** IDB primary key. */
  attachmentId: string;
  messageId: string;
}

// ---------------------------------------------------------------------------
// IDB bootstrap
// ---------------------------------------------------------------------------

function openDb(dbName: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(dbName, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_RECORDS)) {
        db.createObjectStore(STORE_RECORDS, { keyPath: "attachmentId" });
      }
      if (!db.objectStoreNames.contains(STORE_BINDINGS)) {
        db.createObjectStore(STORE_BINDINGS, { keyPath: "attachmentId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ---------------------------------------------------------------------------
// AttachmentBackgroundUploadStore
// ---------------------------------------------------------------------------

/**
 * Persists one upload record per `attachmentId` and enforces the
 * `bindToMessageId` invariant.
 *
 * Construct one instance per JS context. The `dbName` parameter enables test
 * isolation.
 */
export class AttachmentBackgroundUploadStore {
  private readonly dbName: string;
  private dbPromise: Promise<IDBDatabase> | null = null;

  constructor(dbName: string = DEFAULT_DB_NAME) {
    this.dbName = dbName;
  }

  private db(): Promise<IDBDatabase> {
    if (!this.dbPromise) {
      this.dbPromise = openDb(this.dbName);
    }
    return this.dbPromise;
  }

  // -------------------------------------------------------------------------
  // upsert
  // -------------------------------------------------------------------------

  /**
   * Persist (insert or replace) the serialised state for `attachmentId`.
   *
   * If a record already exists for this `attachmentId`, it is overwritten with
   * the new state (unconditional upsert — the caller owns the state machine
   * and is responsible for valid transitions).
   */
  async upsert(attachmentId: string, stateJson: string): Promise<void> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_RECORDS], "readwrite");
      const store = tx.objectStore(STORE_RECORDS);
      const record: StoredUploadRecord = { attachmentId, stateJson };
      const req = store.put(record);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // get
  // -------------------------------------------------------------------------

  /**
   * Retrieve the stored upload record for `attachmentId`, or `undefined` if
   * no record exists.
   */
  async get(attachmentId: string): Promise<StoredUploadRecord | undefined> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_RECORDS], "readonly");
      const store = tx.objectStore(STORE_RECORDS);
      const req = store.get(attachmentId);
      req.onsuccess = () => resolve(req.result as StoredUploadRecord | undefined);
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // getAll
  // -------------------------------------------------------------------------

  /**
   * Return all upload records currently in the store.
   */
  async getAll(): Promise<StoredUploadRecord[]> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_RECORDS], "readonly");
      const store = tx.objectStore(STORE_RECORDS);
      const req = store.getAll();
      req.onsuccess = () => resolve(req.result as StoredUploadRecord[]);
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // delete
  // -------------------------------------------------------------------------

  /**
   * Remove the upload record for `attachmentId`. The binding record (if any)
   * is left intact so the message association is not lost on deletion.
   */
  async delete(attachmentId: string): Promise<void> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_RECORDS], "readwrite");
      const store = tx.objectStore(STORE_RECORDS);
      const req = store.delete(attachmentId);
      req.onsuccess = () => resolve();
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // bindToMessageId — invariant enforcement
  // -------------------------------------------------------------------------

  /**
   * Bind `attachmentId` to `messageId`.
   *
   * - If no binding exists: creates one and returns `{ bound: true }`.
   * - If a binding already exists with the **same** `messageId`: no-op, returns
   *   `{ bound: false }` (idempotent).
   * - If a binding already exists with a **different** `messageId`: throws
   *   `AttachmentBindingConflictError` (the invariant is violated — an
   *   attachment cannot belong to two messages).
   */
  async bindToMessageId(
    attachmentId: string,
    messageId: string,
  ): Promise<{ bound: boolean }> {
    const db = await this.db();

    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_BINDINGS], "readwrite");
      const store = tx.objectStore(STORE_BINDINGS);

      const getReq = store.get(attachmentId);
      getReq.onsuccess = () => {
        const existing = getReq.result as StoredBinding | undefined;
        if (existing !== undefined) {
          if (existing.messageId === messageId) {
            // Idempotent re-bind with same messageId.
            tx.abort();
            resolve({ bound: false });
            return;
          }
          // Different messageId — invariant violation.
          tx.abort();
          reject(
            new AttachmentBindingConflictError(
              attachmentId,
              existing.messageId,
              messageId,
            ),
          );
          return;
        }

        const binding: StoredBinding = { attachmentId, messageId };
        const putReq = store.put(binding);
        putReq.onsuccess = () => resolve({ bound: true });
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
      tx.onerror = () => {
        // tx.abort() fires an error event — suppress it if we already resolved
        // or rejected above.
      };
    });
  }

  // -------------------------------------------------------------------------
  // getBinding
  // -------------------------------------------------------------------------

  /**
   * Return the `messageId` bound to `attachmentId`, or `undefined` if no
   * binding has been set.
   */
  async getBinding(attachmentId: string): Promise<string | undefined> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_BINDINGS], "readonly");
      const store = tx.objectStore(STORE_BINDINGS);
      const req = store.get(attachmentId);
      req.onsuccess = () => {
        const record = req.result as StoredBinding | undefined;
        resolve(record?.messageId);
      };
      req.onerror = () => reject(req.error);
    });
  }

  // -------------------------------------------------------------------------
  // resetAll (test / sign-out helper)
  // -------------------------------------------------------------------------

  /**
   * Delete all records and bindings. Use only in tests or account-wipe flows.
   */
  async resetAll(): Promise<void> {
    const db = await this.db();
    return new Promise((resolve, reject) => {
      const tx = db.transaction([STORE_RECORDS, STORE_BINDINGS], "readwrite");
      tx.objectStore(STORE_RECORDS).clear();
      tx.objectStore(STORE_BINDINGS).clear();
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error);
    });
  }
}

// ---------------------------------------------------------------------------
// Error type
// ---------------------------------------------------------------------------

/**
 * Thrown by `bindToMessageId` when attempting to re-bind an attachment to a
 * different message than the one it is already bound to.
 */
export class AttachmentBindingConflictError extends Error {
  readonly attachmentId: string;
  readonly existingMessageId: string;
  readonly attemptedMessageId: string;

  constructor(
    attachmentId: string,
    existingMessageId: string,
    attemptedMessageId: string,
  ) {
    super(
      `AttachmentBindingConflict: attachmentId "${attachmentId}" is already bound ` +
        `to messageId "${existingMessageId}"; cannot re-bind to "${attemptedMessageId}".`,
    );
    this.name = "AttachmentBindingConflictError";
    this.attachmentId = attachmentId;
    this.existingMessageId = existingMessageId;
    this.attemptedMessageId = attemptedMessageId;
  }
}
